import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Input,
  useToast,
  Text,
  HStack,
  Heading,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Container,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Flex,
  Icon,
  Tooltip,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  useClipboard,
  Code,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  CardHeader,
  Alert,
  AlertIcon,
  AlertTitle,
  Divider
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  ChevronDownIcon,
  ViewIcon, 
  EditIcon, 
  ExternalLinkIcon,
  DeleteIcon,
  CheckIcon,
  CopyIcon,
  LockIcon,
  DownloadIcon
} from '@chakra-ui/icons';
import { 
  FaDollarSign, 
  FaUpload, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaBuilding,
  FaCreditCard,
  FaCalendarAlt,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaChartLine,
  FaReceipt,
  FaArrowRight,
  FaFileCsv,
  FaCopy
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { useOutletContext, useNavigate } from 'react-router-dom';

import { getAuthHeaders } from '../../utils/apiAuth';
const LabManagementCompact = () => {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLab, setSelectedLab] = useState(null);
  
  // Filtering and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // Default 50 per page to reduce load
  const [showAll, setShowAll] = useState(false);
  const [totalLabsCount, setTotalLabsCount] = useState(0);
  const [dashboardStats, setDashboardStats] = useState({ total: 0, active: 0, unclaimed: 0, revenue: 0 });
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const { isOpen: isLabModalOpen, onOpen: onLabModalOpen, onClose: onLabModalClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const adminUser = useOutletContext();

  // Form data state
  const [labFormData, setLabFormData] = useState({
    name: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    fax_phone: '',
    city: '',
    state: '',
    zip_code: '',
    street_address: '',
    provider_number: '',
    partnership_status: 'unclaimed',
    subscription_plan: '',
    subscription_status: 'inactive',
    monthly_fee: 0,
    enrollment_date: '',
    next_billing_date: '',
    is_active: true,
    trial_days: 14
  });

  // Payment details modal state
  const { isOpen: isPaymentModalOpen, onOpen: onPaymentModalOpen, onClose: onPaymentModalClose } = useDisclosure();
  const { isOpen: isSecretKeyModalOpen, onOpen: onSecretKeyModalOpen, onClose: onSecretKeyModalClose } = useDisclosure();
  const [selectedLabForKey, setSelectedLabForKey] = useState(null);
  const [newSecretKey, setNewSecretKey] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [selectedLabForPayment, setSelectedLabForPayment] = useState(null);
  const [syncingSubscription, setSyncingSubscription] = useState(false);
  const [fetchingCustomerId, setFetchingCustomerId] = useState(false);
  const [customerIdInput, setCustomerIdInput] = useState('');
  const [savingCustomerId, setSavingCustomerId] = useState(false);
  const [stripeCharges, setStripeCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [paymentLinkEmail, setPaymentLinkEmail] = useState('');
  const [copyingPaymentLink, setCopyingPaymentLink] = useState(false);

  const handleShowPaymentDetails = (lab) => {
    setSelectedLabForPayment(lab);
    setCustomerIdInput(lab?.stripe_customer_id || '');
    setStripeCharges([]);
    setPaymentLinkEmail(lab?.contact_email || '');
    onPaymentModalOpen();
  };

  const loadStripeCharges = async () => {
    if (!selectedLabForPayment?.stripe_customer_id) return;
    setLoadingCharges(true);
    setStripeCharges([]);
    try {
      const res = await fetch('/api/list-stripe-charges', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ labId: selectedLabForPayment.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load charges');
      setStripeCharges(data.charges || []);
      if (!(data.charges && data.charges.length)) {
        toast({ title: 'No charges found', status: 'info', duration: 3000, isClosable: true });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoadingCharges(false);
    }
  };

  // Send payment update link to lab via email
  const handleSendPaymentLink = async ({ sendEmail = true } = {}) => {
    if (!selectedLabForPayment) return;
    const isEmailSend = sendEmail;
    if (isEmailSend) setSendingPaymentLink(true);
    else setCopyingPaymentLink(true);

    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/send-payment-update-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          labId: selectedLabForPayment.id,
          email: paymentLinkEmail || selectedLabForPayment.contact_email,
          sendEmail: isEmailSend,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create payment link');

      if (isEmailSend) {
        toast({
          title: 'Payment link sent',
          description: data.emailSent
            ? `Email sent to ${data.recipientEmail}`
            : 'Link created but email could not be sent (check email credentials)',
          status: data.emailSent ? 'success' : 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

      if (!isEmailSend && data.url) {
        await navigator.clipboard.writeText(data.url);
        toast({ title: 'Link copied to clipboard', status: 'success', duration: 3000, isClosable: true });
      }
    } catch (err) {
      // If API is unavailable (local dev), offer Stripe Dashboard fallback
      const customerId = selectedLabForPayment.stripe_customer_id;
      if (customerId) {
        const dashboardUrl = `https://dashboard.stripe.com/customers/${customerId}`;
        toast({
          title: 'API not available',
          description: 'This feature requires the deployed server. Opening Stripe Dashboard instead.',
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
        window.open(dashboardUrl, '_blank');
      } else {
        toast({ title: 'Error', description: err.message, status: 'error', duration: 5000, isClosable: true });
      }
    } finally {
      setSendingPaymentLink(false);
      setCopyingPaymentLink(false);
    }
  };

  // Save the real Stripe Customer ID (e.g. pasted from Stripe Dashboard) — via API so service role bypasses RLS
  const handleSaveCustomerId = async () => {
    if (!selectedLabForPayment) return;
    const trimmed = (customerIdInput || '').trim();
    setSavingCustomerId(true);
    try {
      const response = await fetch('/api/update-lab-stripe-customer', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          labId: selectedLabForPayment.id,
          stripeCustomerId: trimmed || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.details || 'Failed to save');

      const updatedLab = { ...selectedLabForPayment, stripe_customer_id: trimmed || null };
      setSelectedLabForPayment(updatedLab);
      await fetchLabs();

      toast({
        title: trimmed ? 'Customer ID saved' : 'Customer ID cleared',
        description: data.message || (trimmed ? `Stripe Customer ID updated for ${selectedLabForPayment.name}.` : 'Stripe Customer ID removed.'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to save Customer ID',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingCustomerId(false);
    }
  };

  // Get or create Stripe customer automatically (by lab email) and save to lab
  const handleGetOrCreateCustomerId = async () => {
    if (!selectedLabForPayment) return;
    setFetchingCustomerId(true);
    try {
      const response = await fetch('/api/get-or-create-stripe-customer', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ labId: selectedLabForPayment.id }),
      });
      const rawText = await response.text();
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          'API returned non-JSON. This endpoint runs on the deployed server (e.g. Vercel). Try on the live site.'
        );
      }
      const data = JSON.parse(rawText);
      if (!response.ok) throw new Error(data.error || data.details || 'Failed to get Customer ID');

      const customerId = data.customerId;
      const updatedLab = { ...selectedLabForPayment, stripe_customer_id: customerId };
      setSelectedLabForPayment(updatedLab);
      setCustomerIdInput(customerId);
      await fetchLabs();

      toast({
        title: data.created ? 'Stripe customer created' : 'Stripe customer found',
        description: data.message || `Customer ID: ${customerId}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to get Customer ID from Stripe',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFetchingCustomerId(false);
    }
  };

  // Sync subscription from Stripe
  const handleSyncSubscription = async () => {
    if (!selectedLabForPayment || !selectedLabForPayment.stripe_customer_id) {
      toast({
        title: 'Error',
        description: 'Lab must have a Stripe Customer ID to sync subscription',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSyncingSubscription(true);
    try {
      const response = await fetch('/api/sync-stripe-subscription', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          labId: selectedLabForPayment.id,
          customerId: selectedLabForPayment.stripe_customer_id,
        }),
      });

      const contentType = response.headers.get('content-type') || '';
      const rawText = await response.text();

      if (!contentType.includes('application/json')) {
        // This usually means we're hitting an HTML page (like the React app or a 404)
        // instead of the Vercel serverless function.
        console.error('Non-JSON response from /api/sync-stripe-subscription:', rawText.slice(0, 200));
        throw new Error(
          'Sync endpoint returned HTML instead of JSON. ' +
          'If you are running locally, this API route only works on the deployed server (Vercel). ' +
          'Try this on the live/staging site where serverless functions are enabled.'
        );
      }

      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseError) {
        console.error('Failed to parse JSON from sync endpoint:', parseError, rawText.slice(0, 200));
        throw new Error('Sync endpoint returned invalid JSON.');
      }

      if (!response.ok) {
        // Show more detailed error message from API
        const errorMsg = data.error || data.details || 'Failed to sync subscription';
        throw new Error(errorMsg);
      }

      if (data.noSubscription) {
        await fetchLabs();
        setSelectedLabForPayment((prev) =>
          prev
            ? {
                ...prev,
                stripe_subscription_id: null,
                subscription_status: 'inactive',
                next_billing_date: null,
              }
            : null
        );
        toast({
          title: 'No subscription in Stripe',
          description: data.message || 'Lab updated to match Stripe (status set to inactive).',
          status: 'info',
          duration: 6000,
          isClosable: true,
        });
        setSyncingSubscription(false);
        return;
      }

      if (!data.subscription) {
        throw new Error('Sync endpoint did not return a subscription object.');
      }

      // Update the selected lab with new subscription data
      const updatedLab = {
        ...selectedLabForPayment,
        stripe_subscription_id: data.subscription.id,
        subscription_status: data.subscription.status,
        next_billing_date: data.subscription.current_period_end
          ? new Date(data.subscription.current_period_end * 1000).toISOString().split('T')[0]
          : null,
      };
      setSelectedLabForPayment(updatedLab);

      // Refresh labs list
      await fetchLabs();

      toast({
        title: 'Success',
        description: `Subscription synced: ${data.subscription.id} (${data.subscription.status})`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error syncing subscription:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to sync subscription from Stripe',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncingSubscription(false);
    }
  };

  // Check if lab has payment details
  const hasPaymentDetails = (lab) => {
    return lab.stripe_customer_id || lab.stripe_subscription_id || lab.subscription_status === 'active';
  };

  // Fetch labs with subscription data
  const fetchLabs = async (page = currentPage, sort = sortBy, order = sortOrder, status = statusFilter, plan = planFilter) => {
    try {
      setLoading(true);

      // Build query with server-side sorting, filtering, and pagination
      let query = supabase
        .from('labs')
        .select('*', { count: 'exact' })
        .order(sort === 'monthly_fee' ? 'monthly_fee' : sort, { ascending: order === 'asc' });

      // Apply status filter server-side
      if (status === 'active') query = query.eq('subscription_status', 'active');
      else if (status === 'unclaimed') query = query.eq('partnership_status', 'unclaimed');
      else if (status === 'inactive') query = query.eq('subscription_status', 'inactive');
      else if (status === 'past_due') query = query.eq('subscription_status', 'past_due');

      // Apply plan filter server-side
      if (plan !== 'all') query = query.eq('subscription_plan', plan);

      // Paginate
      const from = (page - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to);

      // Fetch page + dashboard stats in parallel
      const [pageResult, totalRes, activeRes, unclaimedRes, activeLabsRes] = await Promise.all([
        query,
        supabase.from('labs').select('*', { count: 'exact', head: true }),
        supabase.from('labs').select('*', { count: 'exact', head: true }).eq('subscription_status', 'active'),
        supabase.from('labs').select('*', { count: 'exact', head: true }).eq('partnership_status', 'unclaimed'),
        supabase.from('labs').select('monthly_fee').eq('subscription_status', 'active')
      ]);

      if (pageResult.error) throw pageResult.error;

      setLabs(pageResult.data || []);
      setTotalLabsCount(pageResult.count ?? 0);

      const revenue = (activeLabsRes.data || []).reduce((sum, lab) => sum + (parseFloat(lab.monthly_fee) || 0), 0);
      setDashboardStats({
        total: totalRes.count ?? 0,
        active: activeRes.count ?? 0,
        unclaimed: unclaimedRes.count ?? 0,
        revenue
      });
    } catch (error) {
      console.error('Error fetching labs:', error);
      toast({
        title: 'Error fetching labs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all labs with active subscriptions from database
  const fetchActiveSubscriptionLabs = async () => {
    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .eq('subscription_status', 'active')
        .order('name')
        .limit(25000); // Increased limit to cover all active labs

      if (error) throw error;
      
      setSearchResults(data || []);
      setIsAdvancedSearch(true);
      setStatusFilter('active');
      setCurrentPage(1);
      
      toast({
        title: 'Active Subscriptions Loaded',
        description: `Found ${data?.length || 0} labs with active subscriptions`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error fetching active subscription labs:', error);
      toast({
        title: 'Error Loading Active Subscriptions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Advanced search function that searches across all labs in database
  const performAdvancedSearch = async (query) => {
    if (!query.trim()) {
      setIsAdvancedSearch(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Search across all labs in database using Supabase text search
      // Increased limit to 25000 to cover all 19k+ labs
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .or(`name.ilike.%${query}%,city.ilike.%${query}%,state.ilike.%${query}%,provider_number.ilike.%${query}%,contact_phone.ilike.%${query}%`)
        .order('name')
        .limit(25000); // Increased limit to cover all labs

      if (error) throw error;
      
      setSearchResults(data || []);
      setIsAdvancedSearch(true);
      setCurrentPage(1);
    } catch (error) {
      console.error('Advanced search error:', error);
      toast({
        title: 'Search Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Debounce timer for auto-search
  const searchDebounceTimer = useRef(null);

  // Handle search input with debouncing - automatically search database when typing
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    
    // Clear advanced search if query is empty
    if (!value.trim()) {
      setIsAdvancedSearch(false);
      setSearchResults([]);
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
        searchDebounceTimer.current = null;
      }
      return;
    }

    // Auto-trigger database search after user stops typing (debounced)
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    searchDebounceTimer.current = setTimeout(() => {
      // Automatically search database when user types (searches all 19k+ labs)
      performAdvancedSearch(value);
    }, 500); // Wait 500ms after user stops typing
  };

  // When search is active, use searchResults; otherwise labs are already server-side filtered/sorted
  const displayLabs = isAdvancedSearch ? searchResults : labs;

  // For advanced search, apply client-side status/plan filters on top
  const filteredLabs = isAdvancedSearch ? displayLabs.filter(lab => {
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && lab.subscription_status === 'active') ||
      (statusFilter === 'unclaimed' && lab.partnership_status === 'unclaimed') ||
      (statusFilter === 'inactive' && lab.subscription_status === 'inactive') ||
      (statusFilter === 'past_due' && lab.subscription_status === 'past_due');
    const matchesPlan = planFilter === 'all' || lab.subscription_plan === planFilter;
    return matchesStatus && matchesPlan;
  }) : displayLabs;

  // Pagination — for server-side mode, labs are already one page; for search mode, paginate client-side
  const totalFilteredCount = isAdvancedSearch ? filteredLabs.length : totalLabsCount;
  const effectiveItemsPerPage = (isAdvancedSearch && showAll) ? filteredLabs.length : itemsPerPage;
  const totalPages = Math.ceil(totalFilteredCount / effectiveItemsPerPage);
  const paginatedLabs = isAdvancedSearch
    ? filteredLabs.slice((currentPage - 1) * effectiveItemsPerPage, (currentPage - 1) * effectiveItemsPerPage + effectiveItemsPerPage)
    : filteredLabs; // already paginated server-side

  // Get subscription status color
  const getStatusColor = (status, partnershipStatus) => {
    if (partnershipStatus === 'unclaimed') return 'orange';
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'past_due': return 'red';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  // Get plan color
  const getPlanColor = (plan) => {
    switch (plan) {
      case 'basic': return 'blue';
      case 'standard': return 'purple';
      case 'premium': return 'gold';
      case 'enterprise': return 'teal';
      default: return 'gray';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle lab edit
  const handleEditLab = (lab) => {
    setSelectedLab(lab);
      setLabFormData({
      name: lab.name || '',
      address: lab.address || '',
      contact_email: lab.contact_email || '',
      contact_phone: lab.contact_phone || '',
      fax_phone: lab.fax_phone || '',
      city: lab.city || '',
      state: lab.state || '',
      zip_code: lab.zip_code || '',
      street_address: lab.street_address || '',
      provider_number: lab.provider_number || '',
      partnership_status: lab.partnership_status || 'unclaimed',
      subscription_plan: lab.subscription_plan || '',
      subscription_status: lab.subscription_status || 'inactive',
      monthly_fee: lab.monthly_fee || 0,
      enrollment_date: lab.enrollment_date || '',
      next_billing_date: lab.next_billing_date || '',
        is_active: lab.is_active !== false,
        trial_days: lab.trial_days ?? 14
    });
    onLabModalOpen();
  };

  const handleOpenSecretKey = (lab) => {
    setSelectedLabForKey(lab);
    setNewSecretKey(lab.secret_key || '');
    onSecretKeyModalOpen();
  };

  const setSecretKey = async (labId, secretKey) => {
    const trimmedKey = (secretKey || '').trim();
    if (!trimmedKey) {
      toast({ title: 'Key required', description: 'Please enter a secret key.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setGeneratingKey(true);
    const apiBase = process.env.REACT_APP_API_URL || '';
    try {
      const response = await fetch(`${apiBase}/api/set-lab-secret-key`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ labId, secretKey: trimmedKey }),
      });
      let result;
      try {
        result = await response.json();
      } catch {
        throw new Error('API not available. For local dev run "vercel dev" or set REACT_APP_API_URL to your deployed URL.');
      }
      if (!response.ok) throw new Error(result.error || 'Failed to save key');
      setLabs(prev => prev.map(lab => lab.id === labId ? { ...lab, secret_key: trimmedKey } : lab));
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      if (adminUser?.name) {
        await supabase.from('admin_activity_log').insert({
          action: 'Updated Lab Secret Key',
          username: adminUser.name,
          details: `Lab ID: ${labId}`,
          created_at: new Date().toISOString(),
        });
      }
      toast({ title: 'Secret key updated', description: result.message || 'Lab portal key has been saved.', status: 'success', duration: 3000, isClosable: true });
      onSecretKeyModalClose();
      setNewSecretKey('');
    } catch (error) {
      toast({ title: 'Error updating secret key', description: error.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setGeneratingKey(false);
    }
  };

  // Handle form input changes
  const handleLabInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setLabFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };

      // If subscription status changes to active and no plan is selected, suggest basic plan
      if (name === 'subscription_status' && value === 'active' && !prev.subscription_plan) {
        updated.subscription_plan = 'basic';
        updated.monthly_fee = 99;
      }

      // If subscription status changes away from active, clear plan and fee
      if (name === 'subscription_status' && value !== 'active') {
        updated.subscription_plan = '';
        updated.monthly_fee = 0;
      }

      // Auto-set monthly fee based on plan selection
      if (name === 'subscription_plan') {
        switch (value) {
          case 'basic':
            updated.monthly_fee = 99;
            break;
          case 'standard':
            updated.monthly_fee = 299;
            break;
          case 'premium':
            updated.monthly_fee = 999;
            break;
          case 'enterprise':
            // Keep current fee for enterprise (custom pricing)
            break;
          default:
            updated.monthly_fee = 0;
        }
      }

      return updated;
    });
  };

  // Save lab changes (handles both add and edit)
  const handleLabSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Active subscription status requires a subscription plan
    if (labFormData.subscription_status === 'active' && !labFormData.subscription_plan) {
      toast({
        title: 'Validation Error',
        description: 'Active subscription status requires a subscription plan to be selected.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // If subscription status is not active, clear the plan and fee
    if (labFormData.subscription_status !== 'active') {
      labFormData.subscription_plan = '';
      labFormData.monthly_fee = 0;
    }
    
    try {
      const updateData = { ...labFormData };
      
      // Clean up empty date fields to avoid database errors
      if (!updateData.enrollment_date || updateData.enrollment_date.trim() === '') {
        updateData.enrollment_date = null;
      }
      if (!updateData.next_billing_date || updateData.next_billing_date.trim() === '') {
        updateData.next_billing_date = null;
      }
      
      // Calculate next billing date if enrollment date is set (28-day cycle)
      if (updateData.enrollment_date && updateData.subscription_status === 'active') {
        const enrollment = new Date(updateData.enrollment_date);
        if (!isNaN(enrollment.getTime())) {
          const nextBilling = new Date(enrollment);
          nextBilling.setDate(enrollment.getDate() + 28);
          updateData.next_billing_date = nextBilling.toISOString().split('T')[0];
        }
      }

      let error, data;

      if (selectedLab && selectedLab.id) {
        // Try 1: Direct update with .select() to verify rows were affected
        ({ data, error } = await supabase
          .from('labs')
          .update(updateData)
          .eq('id', selectedLab.id)
          .select());

        if (error) throw error;

        // If .select() returned no rows, RLS may have blocked the update
        if (!data || data.length === 0) {
          // Try 2: API route with service role (works on Vercel)
          let apiWorked = false;
          try {
            const apiBase = process.env.REACT_APP_API_URL || '';
            const res = await fetch(`${apiBase}/api/update-lab`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({ labId: selectedLab.id, updateData }),
            });
            const result = await res.json().catch(() => null);
            if (result && result.success) apiWorked = true;
          } catch { /* API not available */ }

          if (!apiWorked) {
            // Try 3: Direct update without .select() (some RLS configs allow update but block returning rows)
            const { error: retryErr } = await supabase
              .from('labs')
              .update(updateData)
              .eq('id', selectedLab.id);
            if (retryErr) throw retryErr;
          }
        }
      } else {
        // Add new lab
        ({ error } = await supabase
          .from('labs')
          .insert(updateData));
        if (error) throw error;
      }

      toast({
        title: selectedLab ? 'Lab updated successfully' : 'Lab added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onLabModalClose();
      fetchLabs();
    } catch (error) {
      toast({
        title: 'Error updating lab',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (!isAdvancedSearch) {
      fetchLabs(currentPage, sortBy, sortOrder, statusFilter, planFilter);
    }
  }, [currentPage, sortBy, sortOrder, statusFilter, planFilter, itemsPerPage, isAdvancedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Statistics (from database aggregates; dashboardStats updated in fetchLabs)
  const stats = {
    total: dashboardStats.total,
    active: dashboardStats.active,
    unclaimed: dashboardStats.unclaimed,
    revenue: dashboardStats.revenue
  };

  return (
    <Box bg="gray.50" minH="100vh" py={6} px={{ base: 4, md: 6 }}>
      <Container maxW="full" px={0}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={4}>
          <Box>
            <Heading size="lg" color="gray.800" fontWeight="700">
              Laboratory Management
            </Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Manage lab partnerships, subscriptions, and billing
            </Text>
          </Box>
          <HStack spacing={3} flexWrap="wrap">
            <Button
              leftIcon={<Icon as={FaDollarSign} />}
              colorScheme="purple"
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/billing')}
            >
              Billing & Transactions
            </Button>
            <Button
              leftIcon={<Icon as={FaUpload} />}
              colorScheme="blue"
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/labs/upload')}
            >
              Upload Labs
            </Button>
            <Button
              leftIcon={<Icon as={FaBuilding} />}
              colorScheme="green"
              size="sm"
              onClick={() => {
                setSelectedLab(null);
                setLabFormData({
                  name: '',
                  address: '',
                  contact_email: '',
                  contact_phone: '',
                  fax_phone: '',
                  city: '',
                  state: '',
                  zip_code: '',
                  street_address: '',
                  provider_number: '',
                  partnership_status: 'unclaimed',
                  subscription_plan: '',
                  subscription_status: 'inactive',
                  monthly_fee: 0,
                  enrollment_date: '',
                  next_billing_date: '',
                  is_active: true
                });
                onLabModalOpen();
              }}
            >
              Add Lab
            </Button>
          </HStack>
        </Flex>

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4}>
          <Card borderWidth="1px" borderColor="gray.200" shadow="sm" overflow="hidden">
            <CardBody>
              <Flex justify="space-between" align="flex-start">
                <Stat flex={1}>
                  <StatLabel fontSize="xs" color="gray.500" fontWeight="500">Total Labs</StatLabel>
                  <StatNumber fontSize="xl" fontWeight="700">{stats.total.toLocaleString()}</StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>All laboratories</StatHelpText>
                </Stat>
                <Box bg="blue.50" color="blue.600" p={2} borderRadius="lg" borderLeftWidth="3px" borderLeftColor="blue.400">
                  <Icon as={FaBuilding} boxSize={5} />
                </Box>
              </Flex>
            </CardBody>
          </Card>

          <Card borderWidth="1px" borderColor="gray.200" shadow="sm" cursor="pointer" _hover={{ shadow: "md" }} onClick={fetchActiveSubscriptionLabs} overflow="hidden">
            <CardBody>
              <Flex justify="space-between" align="flex-start">
                <Stat flex={1}>
                  <StatLabel fontSize="xs" color="gray.500" fontWeight="500">Active Subscriptions</StatLabel>
                  <StatNumber fontSize="xl" fontWeight="700" color="green.600">{stats.active.toLocaleString()}</StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>Paying customers (click to view)</StatHelpText>
                </Stat>
                <Box bg="green.50" color="green.600" p={2} borderRadius="lg" borderLeftWidth="3px" borderLeftColor="green.400">
                  <Icon as={FaCheckCircle} boxSize={5} />
                </Box>
              </Flex>
            </CardBody>
          </Card>

          <Card borderWidth="1px" borderColor="gray.200" shadow="sm" overflow="hidden">
            <CardBody>
              <Flex justify="space-between" align="flex-start">
                <Stat flex={1}>
                  <StatLabel fontSize="xs" color="gray.500" fontWeight="500">Unclaimed Labs</StatLabel>
                  <StatNumber fontSize="xl" fontWeight="700" color="orange.600">{stats.unclaimed.toLocaleString()}</StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>Not yet enrolled</StatHelpText>
                </Stat>
                <Box bg="orange.50" color="orange.600" p={2} borderRadius="lg" borderLeftWidth="3px" borderLeftColor="orange.400">
                  <Icon as={FaExclamationTriangle} boxSize={5} />
                </Box>
              </Flex>
            </CardBody>
          </Card>

          <Card borderWidth="1px" borderColor="gray.200" shadow="sm" overflow="hidden">
            <CardBody>
              <Flex justify="space-between" align="flex-start">
                <Stat flex={1}>
                  <StatLabel fontSize="xs" color="gray.500" fontWeight="500">Monthly Revenue</StatLabel>
                  <StatNumber fontSize="xl" fontWeight="700" color="green.600">{formatCurrency(stats.revenue)}</StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>28-day cycles</StatHelpText>
                </Stat>
                <Box bg="green.50" color="green.600" p={2} borderRadius="lg" borderLeftWidth="3px" borderLeftColor="green.400">
                  <Icon as={FaDollarSign} boxSize={5} />
                </Box>
              </Flex>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Filters and Search */}
        <Card borderWidth="1px" borderColor="gray.200" shadow="sm">
          <CardHeader py={3} pb={0}>
            <Heading size="sm" fontWeight="600">Search & filters</Heading>
            <Text fontSize="xs" color="gray.500">Search by name, city, state, or provider number; filter by status and plan.</Text>
          </CardHeader>
          <CardBody pt={3}>
            <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr 1fr" }} gap={4}>
              <GridItem>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search labs by name, city, state, or provider number..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        performAdvancedSearch(searchQuery);
                      }
                    }}
                    pr="5.2rem"
                  />
                  <InputRightElement width="5.2rem" right="0.5rem">
                    <Button
                      size="xs"
                      width="100%"
                      fontSize="xs"
                      px={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      whiteSpace="nowrap"
                      colorScheme={isAdvancedSearch ? "green" : "blue"}
                      variant={isAdvancedSearch ? "solid" : "outline"}
                      onClick={() => {
                        if (isAdvancedSearch) {
                          setSearchQuery('');
                          setIsAdvancedSearch(false);
                          setSearchResults([]);
                        } else {
                          performAdvancedSearch(searchQuery);
                        }
                      }}
                      isLoading={isSearching}
                      isDisabled={!searchQuery.trim() && !isAdvancedSearch}
                    >
                      {isAdvancedSearch ? "Clear" : "Search All"}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </GridItem>
              
              <GridItem>
                <Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  icon={<ChevronDownIcon />}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Subscription</option>
                  <option value="unclaimed">Unclaimed</option>
                  <option value="inactive">Inactive</option>
                  <option value="past_due">Past Due</option>
                </Select>
              </GridItem>

              <GridItem>
                <Select
                  size="sm"
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  icon={<ChevronDownIcon />}
                >
                  <option value="all">All Plans</option>
                  <option value="basic">Basic ($99)</option>
                  <option value="standard">Standard ($299)</option>
                  <option value="premium">Premium ($999)</option>
                </Select>
              </GridItem>

              <GridItem>
                <Select
                  size="sm"
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  icon={<ChevronDownIcon />}
                >
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="enrollment_date-desc">Newest First</option>
                  <option value="enrollment_date-asc">Oldest First</option>
                  <option value="monthly_fee-desc">Highest Revenue</option>
                  <option value="monthly_fee-asc">Lowest Revenue</option>
                </Select>
              </GridItem>
            </Grid>
          </CardBody>
        </Card>

        {/* Results Summary and Pagination */}
        <Flex justify="space-between" align="center">
          <Text color="gray.600" fontSize="sm">
            {isAdvancedSearch ? (
              <>Showing {paginatedLabs.length} of {filteredLabs.length} search results (from {totalLabsCount.toLocaleString()} total labs)</>
            ) : (
              <>Showing {paginatedLabs.length} of {totalLabsCount.toLocaleString()} {statusFilter !== 'all' ? statusFilter : ''} labs</>
            )}
          </Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <Text fontSize="xs">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="xs"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              Next
            </Button>
            {isAdvancedSearch && (
              <Button
                size="xs"
                colorScheme={showAll ? "green" : "blue"}
                variant={showAll ? "solid" : "outline"}
                onClick={() => {
                  setShowAll(!showAll);
                  setCurrentPage(1);
                }}
              >
                {showAll ? "Show Pages" : "Show All"}
              </Button>
            )}
            <Select
              size="xs"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
                setShowAll(false);
              }}
              w="100px"
            >
              <option value={50}>50/page</option>
              <option value={100}>100/page</option>
              <option value={200}>200/page</option>
              <option value={500}>500/page</option>
            </Select>
          </HStack>
        </Flex>

        {/* Compact Table */}
        <Card borderWidth="1px" borderColor="gray.200" shadow="sm">
          <CardHeader py={3} pb={0}>
            <Heading size="sm" fontWeight="600">Labs</Heading>
          </CardHeader>
          <CardBody p={0} pt={2}>
            <HStack mb={2} px={4} py={2} spacing={2} bg="blue.50" fontSize="xs" color="blue.600">
              <Icon as={FaArrowRight} />
              <Text>Scroll horizontally to view all columns. Action buttons are always visible on the right.</Text>
            </HStack>
            <TableContainer maxW="100%" overflowX="auto" position="relative">
              <Table size="sm" variant="simple" minW="1200px">
                <Thead bg="gray.50">
                  <Tr>
                    <Th fontSize="xs" py={2} minW="200px">Lab Name</Th>
                    <Th fontSize="xs" py={2} minW="150px">Location</Th>
                    <Th fontSize="xs" py={2} minW="120px">Contact</Th>
                    <Th fontSize="xs" py={2} minW="100px">Status</Th>
                    <Th fontSize="xs" py={2} minW="80px">Plan</Th>
                    <Th fontSize="xs" py={2} minW="80px">Monthly Fee</Th>
                    <Th fontSize="xs" py={2} minW="80px">Enrolled</Th>
                    <Th fontSize="xs" py={2} minW="80px">Trial Days</Th>
                    <Th fontSize="xs" py={2} minW="90px">Next Billing</Th>
                    <Th fontSize="xs" py={2} minW="90px">Provider #</Th>
                    <Th fontSize="xs" py={2} minW="120px">Portal Info</Th>
                    <Th fontSize="xs" py={2} position="sticky" right={0} bg="gray.50" zIndex={2} borderLeft="2px solid" borderColor="blue.200" minW="380px" boxShadow="-2px 0 4px rgba(0,0,0,0.1)">
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td colSpan={11} textAlign="center" py={4}>
                        Loading labs...
                      </Td>
                    </Tr>
                  ) : paginatedLabs.length === 0 ? (
                    <Tr>
                      <Td colSpan={11} textAlign="center" py={4} color="gray.500">
                        No labs found
                      </Td>
                    </Tr>
                  ) : (
                    paginatedLabs.map((lab) => (
                      <Tr key={lab.id} _hover={{ bg: "gray.50" }}>
                        <Td py={2} maxW="200px">
                          <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                            {lab.name}
                          </Text>
                        </Td>
                        <Td py={2} fontSize="xs">
                          {lab.city && lab.state ? `${lab.city}, ${lab.state}` : lab.address ? lab.address.substring(0, 30) + '...' : '-'}
                        </Td>
                        <Td py={2} fontSize="xs">
                          <VStack align="start" spacing={0}>
                            {lab.contact_phone && <Text>{lab.contact_phone}</Text>}
                            {lab.contact_email && <Text color="blue.500" noOfLines={1}>{lab.contact_email}</Text>}
                          </VStack>
                        </Td>
                        <Td py={2}>
                          <Badge 
                            colorScheme={getStatusColor(lab.subscription_status, lab.partnership_status)}
                            size="sm"
                          >
                            {lab.partnership_status === 'unclaimed' ? 'Unclaimed' : lab.subscription_status || 'inactive'}
                          </Badge>
                        </Td>
                        <Td py={2}>
                          {lab.subscription_plan ? (
                            <Badge colorScheme={getPlanColor(lab.subscription_plan)} size="sm">
                              {lab.subscription_plan}
                            </Badge>
                          ) : (
                            <Text fontSize="xs" color="gray.500">-</Text>
                          )}
                        </Td>
                        <Td py={2} fontSize="sm" fontWeight="medium">
                          {lab.monthly_fee ? formatCurrency(lab.monthly_fee) : '-'}
                        </Td>
                        <Td py={2} fontSize="xs">
                          {formatDate(lab.enrollment_date)}
                        </Td>
                        <Td py={2} fontSize="xs">
                          {lab.trial_days ?? '-'}
                        </Td>
                        <Td py={2} fontSize="xs">
                          {formatDate(lab.next_billing_date)}
                        </Td>
                        <Td py={2} fontSize="xs">
                          {lab.provider_number || '-'}
                        </Td>
                        <Td py={2}>
                          <VStack align="start" spacing={1} minW="150px">
                            {lab.secret_key ? (
                              <HStack spacing={1}>
                                <Code fontSize="xs" maxW="100px" isTruncated>
                                  {lab.secret_key}
                                </Code>
                                <Tooltip label="Copy Secret Key" hasArrow>
                                  <IconButton
                                    icon={<CopyIcon />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => {
                                      navigator.clipboard.writeText(lab.secret_key);
                                    }}
                                    aria-label="Copy secret key"
                                  />
                                </Tooltip>
                              </HStack>
                            ) : (
                              <Text fontSize="xs" color="gray.400">No key set</Text>
                            )}
                            <Text fontSize="xs" color="blue.500" isTruncated>
                              {lab.portal_slug ? `portal/${lab.portal_slug}` : 'generic-portal'}
                            </Text>
                          </VStack>
                        </Td>
                        <Td py={2} position="sticky" right={0} bg="white" zIndex={1} borderLeft="2px solid" borderColor="blue.200" boxShadow="-2px 0 4px rgba(0,0,0,0.1)">
                          <HStack spacing={2} flexWrap="wrap" minW="200px">
                            <Button
                              leftIcon={<EditIcon />}
                              size="xs"
                              variant="outline"
                              colorScheme="blue"
                              onClick={() => handleEditLab(lab)}
                              minW="50px"
                              fontSize="xs"
                            >
                              Edit
                            </Button>
                            <Button
                              leftIcon={<ViewIcon />}
                              size="xs"
                              variant="outline"
                              colorScheme="teal"
                              onClick={() => window.open(`/lab-directory/${lab.id}`, '_blank')}
                              minW="60px"
                              fontSize="xs"
                            >
                              Profile
                            </Button>
                            <Button
                              leftIcon={<Icon as={FaCreditCard} />}
                              size="xs"
                              variant="outline"
                              colorScheme={hasPaymentDetails(lab) ? "green" : "gray"}
                              onClick={() => handleShowPaymentDetails(lab)}
                              minW={hasPaymentDetails(lab) ? "75px" : "65px"}
                              fontSize="xs"
                            >
                              {hasPaymentDetails(lab) ? "Pay ✓" : "Pay"}
                            </Button>
                            {lab.short_slug && (
                              <Button
                                leftIcon={<ExternalLinkIcon />}
                                size="xs"
                                variant="outline"
                                colorScheme="purple"
                                onClick={() => window.open(`/lab-tests/${lab.short_slug}`, '_blank')}
                                minW="50px"
                                fontSize="xs"
                              >
                                Tests
                              </Button>
                            )}
                            <Button
                              leftIcon={<Icon as={FaDollarSign} />}
                              size="xs"
                              variant="outline"
                              colorScheme="orange"
                              onClick={() => navigate(`/admin/lab-test-costs/${lab.id}`)}
                              minW="55px"
                              fontSize="xs"
                            >
                              Price
                            </Button>
                            <Button
                              leftIcon={<ExternalLinkIcon />}
                              size="xs"
                              variant="outline"
                              colorScheme="cyan"
                              onClick={() => {
                                const portalUrl = lab.portal_slug 
                                  ? `/lab-portal/${lab.portal_slug}`
                                  : `/lab-portal/login`;
                                window.open(portalUrl, '_blank');
                              }}
                              minW="50px"
                              fontSize="xs"
                            >
                              Portal
                            </Button>
                            <Button
                              leftIcon={<LockIcon />}
                              size="xs"
                              variant="outline"
                              colorScheme={lab.secret_key ? 'orange' : 'blue'}
                              onClick={() => handleOpenSecretKey(lab)}
                              minW="60px"
                              fontSize="xs"
                            >
                              {lab.secret_key ? 'Update Key' : 'Set Key'}
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        {/* Lab Edit Modal */}
        <Modal isOpen={isLabModalOpen} onClose={onLabModalClose} size="2xl">
          <ModalOverlay />
          <ModalContent>
            <form onSubmit={handleLabSubmit}>
              <ModalHeader>
                {selectedLab ? 'Edit Laboratory' : 'Add New Laboratory'}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <Grid templateColumns="repeat(2, 1fr)" gap={4} w="full">
                    <GridItem>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">Lab Name</FormLabel>
                        <Input
                          size="sm"
                          name="name"
                          value={labFormData.name}
                          onChange={handleLabInputChange}
                          placeholder="Laboratory name"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Provider Number</FormLabel>
                        <Input
                          size="sm"
                          name="provider_number"
                          value={labFormData.provider_number}
                          onChange={handleLabInputChange}
                          placeholder="Provider number"
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>

                  <Grid templateColumns="2fr 1fr 1fr" gap={4} w="full">
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">City</FormLabel>
                        <Input
                          size="sm"
                          name="city"
                          value={labFormData.city}
                          onChange={handleLabInputChange}
                          placeholder="City"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">State</FormLabel>
                        <Input
                          size="sm"
                          name="state"
                          value={labFormData.state}
                          onChange={handleLabInputChange}
                          placeholder="State"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">ZIP</FormLabel>
                        <Input
                          size="sm"
                          name="zip_code"
                          value={labFormData.zip_code}
                          onChange={handleLabInputChange}
                          placeholder="ZIP"
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>

                  <Grid templateColumns="repeat(2, 1fr)" gap={4} w="full">
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Phone</FormLabel>
                        <Input
                          size="sm"
                          name="contact_phone"
                          value={labFormData.contact_phone}
                          onChange={handleLabInputChange}
                          placeholder="Phone number"
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Email</FormLabel>
                        <Input
                          size="sm"
                          name="contact_email"
                          type="email"
                          value={labFormData.contact_email}
                          onChange={handleLabInputChange}
                          placeholder="Contact email"
                        />
                      </FormControl>
                    </GridItem>
                  </Grid>

                  <Grid templateColumns="repeat(2, 1fr)" gap={4} w="full">
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Partnership Status</FormLabel>
                        <Select
                          size="sm"
                          name="partnership_status"
                          value={labFormData.partnership_status}
                          onChange={handleLabInputChange}
                        >
                          <option value="unclaimed">Unclaimed</option>
                          <option value="active">Active Partner</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Subscription Status</FormLabel>
                        <Select
                          size="sm"
                          name="subscription_status"
                          value={labFormData.subscription_status}
                          onChange={handleLabInputChange}
                        >
                          <option value="inactive">Inactive</option>
                          <option value="active">Active</option>
                          <option value="past_due">Past Due</option>
                          <option value="cancelled">Cancelled</option>
                        </Select>
                        {labFormData.subscription_status === 'active' && !labFormData.subscription_plan && (
                          <Text fontSize="xs" color="red.500" mt={1}>
                            Active status requires a subscription plan
                          </Text>
                        )}
                      </FormControl>
                    </GridItem>
                  </Grid>

                <Grid templateColumns="repeat(4, 1fr)" gap={4} w="full">
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Subscription Plan</FormLabel>
                        <Select
                          size="sm"
                          name="subscription_plan"
                          value={labFormData.subscription_plan}
                          onChange={handleLabInputChange}
                        >
                          <option value="">No Plan</option>
                          <option value="basic">Basic ($99)</option>
                          <option value="standard">Standard ($299)</option>
                          <option value="premium">Premium ($999)</option>
                          <option value="enterprise">Enterprise (Custom)</option>
                        </Select>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Monthly Fee (28-day)</FormLabel>
                        <NumberInput
                          size="sm"
                          value={labFormData.monthly_fee}
                          onChange={(value) => setLabFormData(prev => ({ ...prev, monthly_fee: parseFloat(value) || 0 }))}
                          min={0}
                          precision={2}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Enrollment Date</FormLabel>
                        <Input
                          size="sm"
                          name="enrollment_date"
                          type="date"
                          value={labFormData.enrollment_date || ''}
                          onChange={handleLabInputChange}
                          placeholder="mm/dd/yyyy"
                        />
                      </FormControl>
                    </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="sm">Trial Days</FormLabel>
                      <NumberInput
                        size="sm"
                        value={labFormData.trial_days ?? 0}
                        onChange={(value) => setLabFormData(prev => ({ ...prev, trial_days: parseInt(value) || 0 }))}
                        min={0}
                        max={60}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </GridItem>
                  </Grid>

                  {selectedLab && (
                    <>
                      <Divider my={2} />
                      <FormControl>
                        <FormLabel fontSize="sm" fontWeight="bold">Test Menu File</FormLabel>
                        {selectedLab.test_menu_file ? (
                          <VStack align="start" spacing={2} mt={2}>
                            <HStack>
                              <Icon as={FaFileCsv} color="blue.500" />
                              <Text fontSize="sm" fontWeight="medium">
                                {selectedLab.test_menu_filename || 'Test Menu File'}
                              </Text>
                              {selectedLab.test_menu_uploaded_at && (
                                <Text fontSize="xs" color="gray.500">
                                  (Uploaded: {new Date(selectedLab.test_menu_uploaded_at).toLocaleDateString()})
                                </Text>
                              )}
                            </HStack>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              leftIcon={<DownloadIcon />}
                              onClick={() => window.open(selectedLab.test_menu_file, '_blank')}
                            >
                              Download Test Menu
                            </Button>
                          </VStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500" fontStyle="italic" mt={2}>
                            No test menu file uploaded yet
                          </Text>
                        )}
                      </FormControl>
                    </>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onLabModalClose} size="sm">
                  Cancel
                </Button>
                <Button colorScheme="blue" type="submit" size="sm">
                  {selectedLab ? 'Update Lab' : 'Add Lab'}
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Secret Key Modal */}
        <Modal
          isOpen={isSecretKeyModalOpen}
          onClose={() => { onSecretKeyModalClose(); setNewSecretKey(''); setSelectedLabForKey(null); }}
          size="md"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Set Secret Key for {selectedLabForKey?.name}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text fontSize="sm" color="gray.600">
                  {selectedLabForKey?.secret_key
                    ? 'Setting a new secret key will replace the current one. The lab will need to use the new key for portal access.'
                    : 'Set a unique secret key for lab portal access. The lab will use this to sign in at their portal.'}
                </Text>
                {selectedLabForKey?.secret_key && (
                  <Alert status="warning">
                    <AlertIcon />
                    <AlertTitle>Current key will be replaced</AlertTitle>
                  </Alert>
                )}
                <FormControl isRequired>
                  <FormLabel>Secret Key</FormLabel>
                  <Input
                    type="password"
                    value={newSecretKey}
                    onChange={(e) => setNewSecretKey(e.target.value)}
                    placeholder="Enter a secure secret key"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newSecretKey.trim()) {
                        e.preventDefault();
                        setSecretKey(selectedLabForKey?.id, newSecretKey);
                      }
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Give this key to the lab; they enter it on the portal login page.
                  </Text>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => { onSecretKeyModalClose(); setNewSecretKey(''); setSelectedLabForKey(null); }}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isLoading={generatingKey}
                isDisabled={!newSecretKey.trim()}
                onClick={() => setSecretKey(selectedLabForKey?.id, newSecretKey)}
              >
                {selectedLabForKey?.secret_key ? 'Update Key' : 'Set Key'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Payment Details Modal */}
        <Modal isOpen={isPaymentModalOpen} onClose={onPaymentModalClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack>
                <Icon as={FaCreditCard} color="green.500" />
                <Text>Payment Details - {selectedLabForPayment?.name}</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedLabForPayment && (
                <VStack spacing={5} align="stretch">
                  {/* Subscription summary — compact row */}
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Subscription</Text>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                      <Box bg="gray.50" p={3} rounded="md">
                        <Text fontSize="xs" color="gray.500">Status</Text>
                        <Badge
                          colorScheme={
                            selectedLabForPayment.subscription_status === 'active' ? 'green' :
                            selectedLabForPayment.subscription_status === 'past_due' ? 'red' : 'gray'
                          }
                          mt={1}
                        >
                          {selectedLabForPayment.subscription_status || 'Not Set'}
                        </Badge>
                      </Box>
                      <Box bg="gray.50" p={3} rounded="md">
                        <Text fontSize="xs" color="gray.500">Plan</Text>
                        <Text fontSize="sm" fontWeight="medium">{selectedLabForPayment.subscription_plan || '—'}</Text>
                      </Box>
                      <Box bg="gray.50" p={3} rounded="md">
                        <Text fontSize="xs" color="gray.500">Monthly</Text>
                        <Text fontSize="sm" fontWeight="semibold" color="green.600">${selectedLabForPayment.monthly_fee ?? '0'}</Text>
                      </Box>
                      <Box bg="gray.50" p={3} rounded="md">
                        <Text fontSize="xs" color="gray.500">Next billing</Text>
                        <Text fontSize="sm">{selectedLabForPayment.next_billing_date || 'Not set'}</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  {/* Stripe — one clear block */}
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Stripe</Text>
                    <Box bg="gray.50" p={4} rounded="lg" borderWidth="1px" borderColor="gray.200">
                      <VStack spacing={4} align="stretch">
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>Customer ID</Text>
                          <Text fontSize="xs" color="gray.500" mb={2}>
                            Get by lab email/name or paste from Stripe (Customers → <Code fontSize="xs">cus_xxx</Code>) and save.
                          </Text>
                          <Flex flexWrap="wrap" gap={2} align="center">
                            <Input
                              size="sm"
                              fontFamily="mono"
                              placeholder="cus_xxxxxxxxxxxxx"
                              value={customerIdInput}
                              onChange={(e) => setCustomerIdInput(e.target.value)}
                              maxW="280px"
                              bg="white"
                            />
                            <Button size="sm" colorScheme="green" onClick={handleGetOrCreateCustomerId} isLoading={fetchingCustomerId} loadingText="Get…">
                              Get from Stripe
                            </Button>
                            <Button size="sm" colorScheme="blue" variant="outline" onClick={handleSaveCustomerId} isLoading={savingCustomerId} loadingText="Save…">
                              Save
                            </Button>
                            {selectedLabForPayment.stripe_customer_id && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => {
                                  navigator.clipboard.writeText(selectedLabForPayment.stripe_customer_id);
                                  toast({ title: 'Copied', status: 'success', duration: 2000, isClosable: true });
                                }}
                              >
                                Copy ID
                              </Button>
                            )}
                          </Flex>
                        </Box>

                        <Box pt={2} borderTopWidth="1px" borderColor="gray.200">
                          <Text fontSize="xs" color="gray.500">Payment method</Text>
                          <Text fontSize="sm">
                            {selectedLabForPayment.stripe_customer_id
                              ? 'On file (Stripe — used for batch charges)'
                              : 'Not set. Add Customer ID above and save.'}
                          </Text>
                        </Box>

                        <Box>
                          <Text fontSize="xs" color="gray.500">Subscription ID</Text>
                          <Flex flexWrap="wrap" gap={2} align="center" mt={1}>
                            <Code fontSize="sm" fontFamily="mono" colorScheme="gray" px={2} py={1}>
                              {selectedLabForPayment.stripe_subscription_id || 'Not set'}
                            </Code>
                            {selectedLabForPayment.stripe_subscription_id && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => {
                                  navigator.clipboard.writeText(selectedLabForPayment.stripe_subscription_id);
                                  toast({ title: 'Copied', status: 'success', duration: 2000, isClosable: true });
                                }}
                              >
                                Copy
                              </Button>
                            )}
                            {selectedLabForPayment.stripe_customer_id && (
                              <Button size="sm" colorScheme="blue" isLoading={syncingSubscription} loadingText="Syncing…" onClick={handleSyncSubscription}>
                                Sync from Stripe
                              </Button>
                            )}
                          </Flex>
                        </Box>

                        <Box>
                          <Text fontSize="xs" color="gray.500">Enrollment</Text>
                          <Text fontSize="sm">{selectedLabForPayment.enrollment_date || 'Not set'}</Text>
                        </Box>
                      </VStack>
                    </Box>
                  </Box>



                  {selectedLabForPayment.stripe_customer_id && (
                    <Box>
                      <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Charges</Text>
                      <Box bg="gray.50" p={4} rounded="lg" borderWidth="1px" borderColor="gray.200">
                        <Button size="sm" colorScheme="teal" variant="outline" onClick={loadStripeCharges} isLoading={loadingCharges} loadingText="Loading…" mb={3}>
                          Load charges from Stripe
                        </Button>
                        {stripeCharges.length > 0 && (
                          <Box overflowX="auto" maxH="240px" overflowY="auto">
                            <Table size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Date</Th>
                                  <Th isNumeric>Amount</Th>
                                  <Th>Status</Th>
                                  <Th></Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {stripeCharges.map((c) => (
                                  <Tr key={c.id}>
                                    <Td fontSize="xs">{new Date(c.created * 1000).toLocaleDateString()}</Td>
                                    <Td isNumeric fontWeight="medium">
                                      {(c.amount / 100).toFixed(2)} {c.currency?.toUpperCase()}
                                    </Td>
                                    <Td>
                                      <Badge size="sm" colorScheme={c.paid ? 'green' : 'gray'}>{c.status}</Badge>
                                    </Td>
                                    <Td>
                                      {c.receipt_url && (
                                        <Button as="a" href={c.receipt_url} target="_blank" rel="noopener noreferrer" size="xs" variant="link" colorScheme="blue">
                                          Receipt
                                        </Button>
                                      )}
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Copy Payment Update Link */}
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Payment Update Link</Text>
                    <Box bg="blue.50" p={4} rounded="lg" borderWidth="1px" borderColor="blue.200">
                      <Text fontSize="sm" color="gray.600" mb={2}>
                        Share this link with the lab so they can update their credit card via Stripe.
                      </Text>
                      <Code fontSize="xs" p={2} rounded="md" display="block" mb={3} wordBreak="break-all">
                        {`${window.location.origin}/update-payment/${selectedLabForPayment.id}`}
                      </Code>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        leftIcon={<Icon as={FaCopy} />}
                        onClick={() => {
                          const link = `${window.location.origin}/update-payment/${selectedLabForPayment.id}`;
                          navigator.clipboard.writeText(link);
                          toast({ title: 'Link copied!', description: 'Payment update link copied to clipboard.', status: 'success', duration: 3000 });
                        }}
                      >
                        Copy Link
                      </Button>
                    </Box>
                  </Box>

                  <Alert status={hasPaymentDetails(selectedLabForPayment) ? 'success' : 'warning'} rounded="md" py={2}>
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">
                        {hasPaymentDetails(selectedLabForPayment) ? 'Payment details configured' : 'Payment not set up'}
                      </AlertTitle>
                      <Text fontSize="xs" opacity={0.9}>
                        {hasPaymentDetails(selectedLabForPayment)
                          ? 'This lab has Stripe payment information.'
                          : 'Add and save a Stripe Customer ID above.'}
                      </Text>
                    </Box>
                  </Alert>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={onPaymentModalClose}>
                Close
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={() => navigate('/admin/billing')}
                leftIcon={<Icon as={FaReceipt} />}
              >
                View Billing History
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
      </Container>
    </Box>
  );
};

export default LabManagementCompact;
